import { useMemo } from 'react';
import { Step, StepButton, StepConnector, StepLabel, Stepper, Tooltip } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import WizardStepperIcon from '../WizardStepperIcon';

import { useStyles } from './useStyles';

const WizardStepper = ({
  className,
  classes,
  activeStep,
  stepsCount,
  onStepClick,
  getStepText,
  vertical,
  nonLinear,
  clickable,
  isStepCompleted,
  isStepDisabled,
  buttonDisabledHint,
  defaultStepIcon,
  largeIcon,
}) => {
  const styles = useStyles({ classes });

  const steps = useMemo(
    () =>
      Array.from({ length: stepsCount }).map((_, index) => ({
        index: String(index),
        text: getStepText ? getStepText(index) : null,
      })),
    [stepsCount, getStepText]
  );
  const displayText = Boolean(getStepText);

  const checkCompleted = (index) => (isStepCompleted ? isStepCompleted(index) : false);
  const checkDisabled = (index) => (isStepDisabled ? isStepDisabled(index) : false);

  const renderLabel = (text) => (
    <StepLabel
      className={styles.stepLabel}
      classes={{
        active: styles.stepLabel_active,
        labelContainer: styles.stepLabel_labelContainer,
        iconContainer: styles.stepLabel_iconContainer,
        label: styles.stepLabel_label,
        alternativeLabel: styles.stepLabel_alternativeLabel,
        completed: styles.stepLabel_completed,
      }}
      StepIconComponent={defaultStepIcon && !largeIcon ? null : WizardStepperIcon}
      StepIconProps={{
        classes: {
          root: defaultStepIcon ? null : styles.stepIcon_root,
          active: defaultStepIcon
            ? null
            : largeIcon
            ? styles.stepIcon_large
            : styles.stepIcon_active,
        },
      }}
    >
      {text}
    </StepLabel>
  );

  return (
    <Stepper
      className={cx(styles.root, className, {
        [styles.displayText]: displayText,
        [styles.horizontal]: !vertical,
        [styles.vertical]: vertical,
      })}
      activeStep={activeStep}
      connector={
        <StepConnector
          className={styles.connector}
          classes={{ line: styles.connector_line }}
        />
      }
      alternativeLabel={displayText && !vertical}
      orientation={vertical ? 'vertical' : 'horizontal'}
      nonLinear={nonLinear}
      data-steps={stepsCount} // HINT: We have CSS style based on this value
    >
      {steps.map((step, index) => (
        <Tooltip
          key={step.index}
          title={checkDisabled(index) && buttonDisabledHint ? buttonDisabledHint : ''}
        >
          <Step
            className={styles.step_root}
            completed={checkCompleted(index)}
            disabled={checkDisabled(index)}
          >
            {clickable ? (
              <StepButton onClick={() => onStepClick(step)}>{renderLabel(step.text)}</StepButton>
            ) : (
              renderLabel(step.text)
            )}
          </Step>
        </Tooltip>
      ))}
    </Stepper>
  );
};

WizardStepper.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  activeStep: PropTypes.number.isRequired,
  stepsCount: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
  getStepText: PropTypes.func,
  vertical: PropTypes.bool,
  nonLinear: PropTypes.bool,
  clickable: PropTypes.bool,
  isStepCompleted: PropTypes.func,
  isStepDisabled: PropTypes.func,
  buttonDisabledHint: PropTypes.string,
  defaultStepIcon: PropTypes.bool,
  largeIcon: PropTypes.bool,
};

WizardStepper.defaultProps = {
  className: null,
  classes: null,
  onStepClick: null,
  getStepText: null,
  vertical: false,
  nonLinear: false,
  clickable: false,
  isStepCompleted: null,
  isStepDisabled: null,
  buttonDisabledHint: null,
  defaultStepIcon: false,
  largeIcon: false,
};

export default WizardStepper;
